/** 管理台菜单单一数据源:侧边栏菜单 / Tabbar 标签标题反查 / 面包屑共用本配置.
 * hidden 项不进侧边栏,仅用于隐藏页面(如新建指南)的标签标题与面包屑。
 */

import {
  CarryOutOutlined,
  DashboardOutlined,
  OrderedListOutlined,
  PictureOutlined,
  ReadOutlined,
  SafetyOutlined,
  SolutionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";

export interface AdminMenuItem {
  path: string;
  name: string;
  icon?: ReactNode;
  /** 限定可见角色;缺省=所有登录用户(申请人也可见) */
  roles?: ("admin" | "expert" | "applicant")[];
  /** 隐藏项:不渲染进侧边栏,仅参与标签标题反查与面包屑 */
  hidden?: boolean;
}

export const ADMIN_HOME_PATH = "/admin/dashboard";

export const ADMIN_MENU: AdminMenuItem[] = [
  {
    path: "/admin/dashboard",
    name: "工作台",
    icon: <DashboardOutlined />,
    roles: ["admin", "expert"],
  },
  {
    path: "/admin/tasks",
    name: "任务中心",
    icon: <CarryOutOutlined />,
  },
  {
    path: "/admin/guides",
    name: "指南管理",
    icon: <ReadOutlined />,
  },
  {
    path: "/admin/guides/new",
    name: "新建指南",
    hidden: true,
  },
  {
    path: "/admin/evaluations",
    name: "评审管理",
    icon: <SolutionOutlined />,
    roles: ["admin", "expert"],
  },
  {
    path: "/admin/templates",
    name: "评价模板",
    icon: <OrderedListOutlined />,
    roles: ["admin"],
  },
  {
    path: "/admin/users",
    name: "用户管理",
    icon: <UserOutlined />,
    roles: ["admin"],
  },
  {
    path: "/admin/roles",
    name: "角色权限",
    icon: <SafetyOutlined />,
    roles: ["admin"],
  },
  {
    path: "/admin/swipers",
    name: "首页轮播",
    icon: <PictureOutlined />,
    roles: ["admin"],
  },
  {
    path: "/admin/guide-fields",
    name: "字段配置",
    icon: <OrderedListOutlined />,
    roles: ["admin"],
  },
  // /admin 与工作台同页(DashboardPage 双路由),归一标签/面包屑身份
  { path: "/admin", name: "工作台", hidden: true },
];

/** /admin 归一到 /admin/dashboard,避免同页开出两个标签 */
export function normalizeAdminPath(pathname: string): string {
  return pathname === "/admin" ? ADMIN_HOME_PATH : pathname;
}

/** 按路径反查菜单项(含 hidden),用于路由变化时开标签 */
export function findAdminMenuItem(pathname: string): AdminMenuItem | undefined {
  const p = normalizeAdminPath(pathname);
  return ADMIN_MENU.find((m) => m.path === p);
}

/** 侧边栏叶子激活判定:精确命中,或当前路径是其子路径(如 /admin/guides/new 激活指南管理) */
export function isAdminMenuActive(item: AdminMenuItem, pathname: string): boolean {
  const p = normalizeAdminPath(pathname);
  return item.path === p || p.startsWith(`${item.path}/`);
}

export interface AdminBreadcrumb {
  name: string;
  path: string;
}

/** 面包屑:工作台(首页) → 当前路径逐级;首页只显工作台 */
export function getAdminBreadcrumbs(pathname: string): AdminBreadcrumb[] {
  const home: AdminBreadcrumb = { name: "工作台", path: ADMIN_HOME_PATH };
  const p = normalizeAdminPath(pathname);
  if (p === ADMIN_HOME_PATH) return [home];

  const crumbs: AdminBreadcrumb[] = [home];
  const segments = p.split("/").filter(Boolean);
  for (let i = 1; i <= segments.length; i++) {
    const partial = `/${segments.slice(0, i).join("/")}`;
    const item = ADMIN_MENU.find((m) => m.path === partial);
    if (item && item.path !== "/admin" && item.path !== ADMIN_HOME_PATH) {
      crumbs.push({ name: item.name, path: item.path });
    }
  }
  return crumbs;
}
